import * as vp from 'vopay';
import {
  Box,
  Card,
  Badge,
  DropdownMenu,
  Checkbox,
  Icon,
  Toast,
  useUpdateQueryParams,
  usePagination,
  Pagination,
  MaterialSymbol,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import ApprovalInboxSearchForm from './ApprovalInboxSearchForm';
import styles from './ApprovalInbox.module.scss';
import ApprovalInboxContext, { ApprovalInboxCtx } from './Dialogue/ApprovalInboxContext';
import ApprovalInboxDialogue from './Dialogue/ApprovalInboxDialogue';
import { ApprovalInboxDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import useFetchApprovalTransactionsInbox from '@/hooks/useApi/useFetchApprovalTransactionsInbox';
import usePostUnapproveApprovalTransaction from '@/hooks/useApi/usePostUnapproveApprovalTransaction';
import usePostApproveApprovalTransaction from '@/hooks/useApi/usePostApproveApprovalTransaction';
import usePostRejectApprovalTransaction from '@/hooks/useApi/usePostRejectApprovalTransaction';
import useFetchDocument from '@/hooks/useApi/useFetchDocument';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import { useDialog } from '@/providers/Dialog.provider';
import { Trans, useTranslation } from 'react-i18next';
import RejectTransactionDialog from './RejectTransactionDialog';

import { TableFiltersContainer, Button, Table, PageSizeSelector, Title, TransactionTypeLabel } from 'UI';
import type { PageSize } from 'UI';
import type { TableHandle } from 'vopay';
import { format, subYears } from 'date-fns';

type QueryParams = {
  currency: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
};

type ApprovalInboxSearchParams = {
  currency?: string;
  searchTerm?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
};

type FC = FunctionComponent;

export type SelectableApprovalInboxTransaction = vp.ApprovalInboxTransaction & {
  id: string;
  isSelected: boolean;
  RejectionReason?: string;
};
type MyApprovalInboxTransactionTableRow = SelectableApprovalInboxTransaction & { index: number };
type ActionsDropdownProps = {
  transaction: SelectableApprovalInboxTransaction;
};
const ApprovalInboxContainer: FC = () => {
  const dialog = useDialog();
  const { response } = useClientData();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const [approvalTransactions, setApprovalTransactions] = useState<SelectableApprovalInboxTransaction[]>([]);
  const [selectedApprovalTransactions, setSelectedApprovalTransactions] = useState<
    SelectableApprovalInboxTransaction[]
  >([]);
  const [filteredList, setFilteredList] = useState<SelectableApprovalInboxTransaction[]>([]);
  const [selectedUnapproveTransactions, setSelectedUnapproveTransactions] = useState<
    SelectableApprovalInboxTransaction[]
  >([]);
  const [allCheckbox, setAllCheckbox] = useState(false);
  const [numberOfTransactions, setNumberOfTransactions] = useState(0);
  const [showApproved, setShowApproved] = useState(false);
  const [showRecjected, setShowRejected] = useState(false);
  const [showUnapproved, setShowUnapproved] = useState(false);
  const [hasSelectedUnapproved, setHasSelectedUnapproved] = useState(false);
  const [showUnapprovedTransactions, setShowUnapprovedTransactions] = useState(false);
  const [onlyUnapproved, setOnlyUnapproved] = useState(false);
  const [approvalTransactionInbox, , , fetchApprovalTransactionsInbox] = useFetchApprovalTransactionsInbox({});
  const { transactionsReset, approvalListReset } = useApprovalWorkflow();
  const [, , unapproveTransaction] = usePostUnapproveApprovalTransaction({});
  const [, , approveTransaction] = usePostApproveApprovalTransaction({});
  const [, , rejectTransaction] = usePostRejectApprovalTransaction({});
  const { fetchDocument } = useFetchDocument();
  const [approvalInboxError, setApprovalInboxError] = useState('');
  const [showApprovalInboxErrorAlert, setShowApprovalInboxErrorAlert] = useState(false);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Inbox' });

  const getJwtForAccount = async (accountId: string): Promise<string> => {
    if (!accountId || accountId === response.AccountID) return response.JWT || '';
    const subAccountRes = await fetchSubAccountsAsync({
      SearchText: accountId,
      ShowToken: true,
    });
    const subAccount = Object.values(subAccountRes.Subaccounts || {}).find((s) => s.AccountID === accountId);
    return subAccount?.Token ?? '';
  };

  const location = useLocation();

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      currency: params.get('currency') || '',
      searchTerm: params.get('searchTerm') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);

  const currency = useMemo(() => queryParams.currency || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);
  const startDate = useMemo(() => queryParams.startDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.endDate || '', [queryParams]);
  const filtersApplied = useMemo(() => Object.keys(queryParams).length > 0, [queryParams]);

  const timeDifference = format(subYears(new Date(), 1), 'yyyy-MM-dd');
  const currentDateTime = format(new Date(), 'yyyy-MM-dd');

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const alertDialog = useAlertDialog();

  const [pageSize, setPageSize] = useState<PageSize>('20');

  type SortState = { id: string; order: 'asc' | 'desc' | 'none' };
  const [sortState, setSortState] = useState<SortState | null>(null);
  const tableHandle = useRef<TableHandle>(null);

  // pagination
  const totalDataLength = useMemo(() => {
    return approvalTransactionInbox?.PaginationMetaData?.TotalRecords || 0;
  }, [approvalTransactionInbox?.PaginationMetaData?.TotalRecords]);

  const effectiveLimit = pageSize === 'all' ? Math.max(totalDataLength, 1) : parseInt(pageSize, 10);

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: effectiveLimit,
  });

  const handlePageSizeChange = (newPageSize: PageSize) => {
    setPageSize(newPageSize);
    const limit = newPageSize === 'all' ? totalDataLength : parseInt(newPageSize, 10);
    const offset = setPageIndex(0);
    search(offset, { currency, searchTerm }, limit);
  };

  const approvalInboxCtx: ApprovalInboxCtx = {
    showApprovalInboxErrorAlert,
    approvalInboxError,
    showUnapprovedTransactions,
  };
  const search = async (offset = 0, searchParams: ApprovalInboxSearchParams = {}, limit?: number) => {
    const defaultValues = {
      StartDateTime: moment(searchParams.timeRange?.[0] ?? (startDate || timeDifference))
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      EndDateTime: moment(searchParams.timeRange?.[1] ?? (endDate || currentDateTime))
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      Currency: searchParams.currency ?? currency,
      SearchText: searchParams.searchTerm ?? searchTerm,
      Limit: limit ?? effectiveLimit,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      setSortState(null);
      tableHandle.current?.resetSort();
      const res = await fetchApprovalTransactionsInbox(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);

      setApprovalTransactions([
        ...Object.values(res.Transactions || {}).map((transaction, index) => ({
          ...transaction,
          id: index.toString(),
          isSelected: false,
        })),
      ]);
      setFilteredList([
        ...Object.values(res.Transactions || {}).map((transaction, index) => ({
          ...transaction,
          id: index.toString(),
          isSelected: false,
        })),
      ]);
      setAllCheckbox(false);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('errorMessage', {
              defaultValue: 'An error occurred while processing your request. Please try again later.',
            });
      alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), msg);
      console.error(err);
    }
  };
  const unapproveTransactions = async (transactions: SelectableApprovalInboxTransaction[]) => {
    try {
      for (const transaction of transactions) {
        const res = await unapproveTransaction(
          { TransactionID: transaction.ID },
          await getJwtForAccount(transaction.AccountID),
        );
        if (res.Success === false) {
          setApprovalInboxError(res.ErrorMessage);
          setShowApprovalInboxErrorAlert(true);
        }
      }
      search();
      transactionsReset();
      approvalListReset();
      setShowUnapproved(true);
    } catch (error) {
      console.error('Unapprove transactions failed:', error);
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Error' }),
        t('errorMessage', { defaultValue: 'An error occurred while processing your request. Please try again later.' }),
      );
    }
  };
  const approveTransactions = async (transactions: SelectableApprovalInboxTransaction[]) => {
    try {
      for (const transaction of transactions) {
        const res = await approveTransaction(
          { TransactionID: transaction.ID },
          await getJwtForAccount(transaction.AccountID),
        );
        if (res.Success === false) {
          setApprovalInboxError(res.ErrorMessage);
          setShowApprovalInboxErrorAlert(true);
        }
      }
      search();
      transactionsReset();
      approvalListReset();
      setShowApproved(true);
    } catch (error) {
      console.error('Approve transactions failed:', error);
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Error' }),
        t('errorMessage', { defaultValue: 'An error occurred while processing your request. Please try again later.' }),
      );
    }
  };
  const rejectTransactions = async (transactions: SelectableApprovalInboxTransaction[]) => {
    try {
      for (const transaction of transactions) {
        const res = await rejectTransaction(
          { TransactionID: transaction.ID, Reason: transaction.RejectionReason },
          await getJwtForAccount(transaction.AccountID),
        );
        if (res.Success === false) {
          setApprovalInboxError(res.ErrorMessage);
          setShowApprovalInboxErrorAlert(true);
        }
      }
      search();
      transactionsReset();
      approvalListReset();
      setShowRejected(true);
    } catch (error) {
      console.error('Reject transactions failed:', error);
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Error' }),
        t('errorMessage', { defaultValue: 'An error occurred while processing your request. Please try again later.' }),
      );
    }
  };

  const handleDownloadDocument = async (documentID: string) => {
    try {
      const res = await fetchDocument({ DocumentID: documentID });
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = res.Document;
      link.download = ''; // Let the browser use the filename from the URL
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('DownloadDocument.errorMessage', { defaultValue: 'Failed to download document' });
      alertDialog.error(t('DownloadDocument.errorTitle', { defaultValue: 'Error' }), msg);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
      setShouldShowEndDateError(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [startDate, endDate]);

  const handlers = {
    handleWorkflowDialogue: (handlerProps: DialogueHandlerProps<ApprovalInboxDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: ApprovalInboxDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowApprovalInboxErrorAlert(false);
            },
          },
          cancel: {
            default: () => {
              setShowApprovalInboxErrorAlert(false);
            },
          },
        },
        unapprove: {
          confirm: {
            default: () => {
              setShowUnapprovedTransactions(false);

              unapproveTransactions(selectedUnapproveTransactions);
            },
          },

          cancel: {
            default: () => {
              setShowUnapprovedTransactions(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      currency,
      searchTerm,
      startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
      endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  useEffect(() => {
    if (showApproved) {
      setShowApproved(false);
    }
  }, [showApproved]);
  useEffect(() => {
    if (showRecjected) {
      setShowRejected(false);
    }
  }, [showRecjected]);
  useEffect(() => {
    if (showUnapproved) {
      setShowUnapproved(false);
    }
  }, [showUnapproved]);

  const filterResults = () => {
    const keys = ['FullName', 'FileName', 'Amount', 'NumberPayments'];
    setFilteredList(
      approvalTransactions.filter(
        (data) =>
          searchTerm === '' ||
          keys.some((key) =>
            data[key as keyof vp.ApprovalInboxTransaction]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
          ),
      ),
    );
  };
  const onCheckedTransaction = (id: string) => {
    setApprovalTransactions(
      approvalTransactions.map((transaction) => {
        if (transaction.id === id) {
          return { ...transaction, isSelected: !transaction.isSelected };
        }
        return transaction;
      }),
    );
  };
  const checkToggleTransactions = () => {
    setApprovalTransactions(
      approvalTransactions.map((transaction) => ({
        ...transaction,
        isSelected: !allCheckbox,
      })),
    );
    setAllCheckbox(!allCheckbox);
  };
  const uncheckAllTransactions = () => {
    setApprovalTransactions(
      approvalTransactions.map((transaction) => ({
        ...transaction,
        isSelected: false,
      })),
    );
    setAllCheckbox(false);
  };
  const checkAllTransactions = () => {
    setApprovalTransactions(
      approvalTransactions.map((transaction) => ({
        ...transaction,
        isSelected: true,
      })),
    );
    setAllCheckbox(true);
  };
  useEffect(() => {
    const selectedTransactions = filteredList.filter((transaction) => transaction.isSelected);
    const appovedTransationsSelected = selectedTransactions.filter((transaction) => transaction.Approved);
    setHasSelectedUnapproved(appovedTransationsSelected.length > 0);
    setOnlyUnapproved(
      appovedTransationsSelected.length > 0 && appovedTransationsSelected.length === selectedTransactions.length,
    );
    setSelectedApprovalTransactions([...selectedTransactions]);
  }, [filteredList]);
  useEffect(() => {
    filterResults();
  }, [approvalTransactions]);
  useEffect(() => {
    setAllCheckbox(!approvalTransactions.some((approvalTransaction) => !approvalTransaction.isSelected));
  }, [approvalTransactions]);
  useEffect(() => {
    setApprovalTransactions([
      ...Object.values(approvalTransactionInbox?.Transactions || {}).map((transaction, index) => ({
        ...transaction,
        id: index.toString(), // Add a comma here
        isSelected: false,
      })),
    ]);
  }, [approvalTransactionInbox?.Transactions]);
  const sortedFilteredList = useMemo(() => {
    if (!sortState) return filteredList;
    const sorted = [...filteredList];
    sorted.sort((a, b) => {
      let valA: string | number = '';
      let valB: string | number = '';
      switch (sortState.id) {
        case 'createdOn':
          valA = a.TransactionDate || '';
          valB = b.TransactionDate || '';
          break;
        case 'transactionId':
          valA = a.TransactionType === 'eLinx' ? a.ELinxID || '' : a.ID || '';
          valB = b.TransactionType === 'eLinx' ? b.ELinxID || '' : b.ID || '';
          break;
        case 'name':
          valA = a.FullName || '';
          valB = b.FullName || '';
          break;
        case 'account':
          valA = a.AccountName || '';
          valB = b.AccountName || '';
          break;
        case 'paymentRail':
          valA = a.TransactionType || '';
          valB = b.TransactionType || '';
          break;
        case 'amount':
          valA = Number(a.Amount) || 0;
          valB = Number(b.Amount) || 0;
          break;
        case 'decision':
          valA = a.Approved ? 1 : 0;
          valB = b.Approved ? 1 : 0;
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortState.order === 'asc' ? -1 : 1;
      if (valA > valB) return sortState.order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredList, sortState]);

  const tableProps: vp.TableProps<MyApprovalInboxTransactionTableRow> = {
    id: 'scheduled-transactions-list',
    version: '2',
    zebra: true,
    onSort: (params) => {
      if (pageSize !== 'all') return;
      setSortState(params.order === 'none' ? null : { id: String(params.id), order: params.order });
    },
    columns: [
      {
        id: 'checkbox',
        header: (
          <Checkbox
            name="select-all-invoices"
            variant="border"
            value={allCheckbox}
            onBlur={() => {}}
            onChange={checkToggleTransactions}
            size="sm"
            hasNoPadding
          />
        ),
        align: 'left',
        template: ({ data }) => (
          <Checkbox
            variant="border"
            name={`select-invoice-${data.id || ''}`}
            value={data.isSelected}
            onBlur={() => {}}
            onChange={() => onCheckedTransaction(data.id)}
            size="sm"
            hasNoPadding
          />
        ),
      },

      {
        id: 'createdOn',
        header: t('Table.createdOn', { defaultValue: 'Created On' }),
        align: 'left',
        field: 'TransactionDate',
        sortable: pageSize === 'all',
      },
      {
        id: 'transactionId',
        header: t('Table.transactionId', {
          defaultValue: 'Trans. ID',
        }),
        align: 'left',

        template: ({ data }) => {
          if (data.TransactionType == 'eLinx') {
            return (
              <Table.Link
                to={{
                  pathname: `/elinx-history/details/${data.ELinxID}/`,
                }}
              >
                {data.ELinxID}
              </Table.Link>
            );
          }
          return (
            <Table.Link
              to={{
                pathname: `/transactions-history/details/${data.TransactionType}/${data.ID}/${data.Currency}/`,
              }}
            >
              {data.ID}
            </Table.Link>
          );
        },
      },
      {
        id: 'name',
        header: t('Table.name', { defaultValue: 'Name' }),
        align: 'left',
        field: 'FullName',
        sortable: pageSize === 'all',
      },
      {
        id: 'account',
        header: t('Table.account', {
          defaultValue: 'Account',
        }),
        align: 'left',
        field: 'AccountName',
      },
      {
        id: 'paymentRail',
        header: t('Table.paymentRail', { defaultValue: 'Payment Rail' }),
        align: 'left',
        template: (row) => <TransactionTypeLabel transactionType={row.data.TransactionType ?? ''} />,
        sortable: pageSize === 'all',
      },
      {
        id: 'amount',
        header: t('Table.amount', { defaultValue: 'Amount' }),
        align: 'left',

        template: ({ data }) => `$${vp.formattedCurrency(data.Amount.toString())} ${data.Currency}`,
      },
      {
        id: 'status',
        header: t('Table.status', { defaultValue: 'Transaction Status' }),
        align: 'left',

        template: () => (
          <Badge
            label={t('Table.Row.statusBadge', { defaultValue: 'Pending Approval' })}
            hasIcon
            color={vp.GetColorStatus('pending approval')}
          />
        ),
      },
      {
        id: 'decision',
        header: t('Table.decision', { defaultValue: 'Decision' }),
        align: 'left',

        template: ({ data }) => (
          <Badge
            label={
              data?.Approved
                ? t('Table.Row.decisionApprovedBadge', { defaultValue: 'Approved' })
                : t('Table.Row.decisionUnapprovedBadge', { defaultValue: 'Unapproved' })
            }
            hasIcon
            color={vp.GetColorStatus(data?.Approved ? 'approved' : 'unapproved')}
          />
        ),
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe" ai="center">
            {data.DocumentID && (
              <Button
                size="small"
                colorScheme="secondary"
                iconLeftProps={{
                  icon: 'download_2',
                  fillAmount: 1,
                }}
                onClick={() => handleDownloadDocument(data.DocumentID!)}
              >
                {t('Table.Row.DropdownTable.downloadInvoice', {
                  defaultValue: 'Download Invoice',
                })}
              </Button>
            )}
            <ActionsDropdown transaction={data} />
          </Box>
        ),
      },
    ],
    rows: sortedFilteredList.map((transaction, index) => ({
      id: String(transaction.ID || transaction.FileName || Math.random()),
      data: { ...transaction, index },
    })),
  };

  return (
    <ApprovalInboxContext.Provider value={approvalInboxCtx}>
      <Box pb="xmd" jc="sb" base="8" className={styles.container}>
        <Title level="1">{t('title', { defaultValue: 'Approval Inbox' })}</Title>
      </Box>
      <Card
        style={{
          padding: '24px 16px',
        }}
      >
        <Box flx="col" gap="sm">
          <TableFiltersContainer filtersApplied={filtersApplied}>
            <div className={styles.tableFiltersRow}>
              <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} totalNumResults={totalDataLength} />
              <TableFiltersContainer.Trigger />
            </div>
            <TableFiltersContainer.Content>
              <ApprovalInboxSearchForm
                currency={currency}
                searchTerm={searchTerm}
                hasInvalidEndDate={hasInvalidEndDate}
                shouldShowEndDateError={shouldShowEndDateError}
                setShouldShowEndDateError={setShouldShowEndDateError}
                initialTimeRange={[
                  moment(startDate || timeDifference).valueOf() as vp.Timestamp,
                  moment(endDate || currentDateTime).valueOf() as vp.Timestamp,
                ]}
                search={(searchParams) => {
                  const offset = setPageIndex(0);
                  updateQueryParams({
                    currency: searchParams.currency || '',
                    searchTerm: searchParams.searchTerm || '',
                    startDate: moment(searchParams.timeRange?.[0]).format('YYYY-MM-DD'),
                    endDate: moment(searchParams.timeRange?.[1]).format('YYYY-MM-DD'),
                  });
                  search(offset, searchParams);
                }}
              />
            </TableFiltersContainer.Content>
          </TableFiltersContainer>
        </Box>
        <Box flx="col" mb="sm" mt="lg" position="relative">
          {selectedApprovalTransactions.length > 0 && (
            <div className={styles.selectedBar}>
              <Box ps="sm" pe="sm" pt="sm" pb="sm" jc="sb" ai="center">
                <Box gap="none" ai="center">
                  <div>
                    {selectedApprovalTransactions.length}{' '}
                    {t('ActionsHeader.itemsSelected', { defaultValue: 'Item(s) Selected' })}
                  </div>
                  {selectedApprovalTransactions.length !== approvalTransactions.length && (
                    <>
                      <Icon height="28px" width="25px" icon="line" />
                      <Button
                        colorScheme="primary"
                        size="small"
                        onClick={() => {
                          checkAllTransactions();
                        }}
                      >
                        <span className={styles.selectedBarSelectAll}>
                          {t('ActionsHeader.selectAll', { defaultValue: 'Select All' })} ({approvalTransactions.length})
                        </span>
                      </Button>
                    </>
                  )}
                </Box>
                <Box gap="none">
                  <Box gap="sm">
                    {!onlyUnapproved && (
                      <Button
                        colorScheme="secondary"
                        id="bulk-approve"
                        size="small"
                        onClick={() => {
                          // code to collect
                          const SelectedToSend = selectedApprovalTransactions.filter(
                            (transaction) => vp.toBool(transaction.Approved) === false,
                          );
                          approveTransactions(SelectedToSend);
                          setNumberOfTransactions(SelectedToSend.length);
                        }}
                      >
                        {t('ActionsHeader.bulkApprove', { defaultValue: 'Bulk Approve' })}
                      </Button>
                    )}

                    {!hasSelectedUnapproved && (
                      <Button
                        colorScheme="secondary"
                        id="bulk-reject"
                        size="small"
                        onClick={() => {
                          // code to collect
                          const SelectedToSend = selectedApprovalTransactions.filter(
                            (transaction) => vp.toBool(transaction.Approved) === false,
                          );
                          rejectTransactions(SelectedToSend);
                          setNumberOfTransactions(SelectedToSend.length);
                        }}
                      >
                        {t('ActionsHeader.bulkReject', { defaultValue: 'Bulk Reject' })}
                      </Button>
                    )}
                    {hasSelectedUnapproved && (
                      <Button
                        colorScheme="secondary"
                        id="bulk-unapprove"
                        size="small"
                        onClick={() => {
                          // code to collect
                          const SelectedToSend = selectedApprovalTransactions.filter(
                            (transaction) => vp.toBool(transaction.Approved) === true,
                          );
                          setNumberOfTransactions(SelectedToSend.length);
                          unapproveTransactions(SelectedToSend);
                        }}
                      >
                        {t('ActionsHeader.bulkUnapprove', { defaultValue: 'Bulk Unapprove' })}
                      </Button>
                    )}
                  </Box>
                  <Icon height="28px" width="25px" icon="line" />
                  <Button
                    colorScheme="primary"
                    size="small"
                    id="bulk-cancel"
                    onClick={() => {
                      uncheckAllTransactions();
                    }}
                  >
                    {t('ActionsHeader.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                </Box>
              </Box>
            </div>
          )}
        </Box>
        <Box flx="col" mb="lg" position="relative">
          <Table ref={tableHandle} {...tableProps} />
        </Box>
        <Box width="100%" jc="center" mt="lg">
          {pageCount > 1 && pageSize !== 'all' && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                const offset = setPageIndex(e.selected);
                const searchParams = {
                  currency,
                  searchTerm,
                  startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
                  endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
                };
                updateQueryParams(searchParams);
                search(offset, searchParams);
              }}
            />
          )}
        </Box>
      </Card>
      <Toast
        label={
          <Trans
            i18nKey="TransactionApproval.Inbox.Toast.approved"
            ns="translations"
            components={[<span style={{ fontWeight: 700 }} />]}
            values={{ numberOfTransactions }}
          />
        }
        isVisible={showApproved}
      />
      <Toast
        label={
          <Trans
            i18nKey="TransactionApproval.Inbox.Toast.rejected"
            ns="translations"
            components={[<span style={{ fontWeight: 700 }} />]}
            values={{ numberOfTransactions }}
          />
        }
        isVisible={showRecjected}
        variant="neutral"
      />
      <Toast
        label={
          <Trans
            i18nKey="TransactionApproval.Inbox.Toast.unapproved"
            ns="translations"
            components={[<span style={{ fontWeight: 700 }} />]}
            values={{ numberOfTransactions }}
          />
        }
        isVisible={showUnapproved}
        variant="neutral"
      />
      <ApprovalInboxDialogue handleDialogue={handlers.handleWorkflowDialogue} />
    </ApprovalInboxContext.Provider>
  );

  function ActionsDropdown({ transaction }: ActionsDropdownProps) {
    const itemStyle = { gap: '21px' } as React.CSSProperties;

    return (
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <Button size="small" colorScheme="secondary" iconRight="keyboard_arrow_down">
            {t('Table.Row.DropdownTable.label', { defaultValue: 'Actions' })}
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content align="end">
          {!transaction.Approved && (
            <>
              <DropdownMenu.Item
                onSelect={() => {
                  approveTransactions([transaction]);
                  setNumberOfTransactions(1);
                }}
                left={<MaterialSymbol icon="check_circle" size={18} />}
                style={itemStyle}
              >
                {t('Table.Row.DropdownTable.approve', { defaultValue: 'Approve' })}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={async () => {
                  const rejectionReason = await dialog.open<string>(RejectTransactionDialog);
                  if (rejectionReason === null) return;

                  const transactionWithReason = { ...transaction, RejectionReason: rejectionReason };
                  rejectTransactions([transactionWithReason]);
                  setNumberOfTransactions(1);
                }}
                colorScheme="destructive"
                left={<MaterialSymbol icon="cancel" size={18} />}
                style={itemStyle}
              >
                {t('Table.Row.DropdownTable.reject', { defaultValue: 'Reject' })}
              </DropdownMenu.Item>
            </>
          )}
          {transaction.Approved ? (
            <>
              <DropdownMenu.Item
                onSelect={() => {
                  setNumberOfTransactions(1);
                  setShowUnapprovedTransactions(true);
                  setSelectedUnapproveTransactions([transaction]);
                }}
                left={<MaterialSymbol icon="remove" size={18} />}
                style={itemStyle}
              >
                {t('Table.Row.DropdownTable.unapprove', { defaultValue: 'Unapprove' })}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={async () => {
                  const rejectionReason = await dialog.open<string>(RejectTransactionDialog);
                  if (rejectionReason !== null) {
                    // First unapprove, then reject
                    try {
                      const res = await unapproveTransaction(
                        { TransactionID: transaction.ID },
                        await getJwtForAccount(transaction.AccountName),
                      );

                      const transactionWithReason = { ...transaction, RejectionReason: rejectionReason };
                      if (res.Success === false) {
                        setApprovalInboxError(res.ErrorMessage);
                        setShowApprovalInboxErrorAlert(true);
                        return;
                      }
                      rejectTransactions([transactionWithReason]);
                      setNumberOfTransactions(1);
                    } catch (error) {
                      console.error('Unapprove before reject failed:', error);
                      alertDialog.error(
                        t('errorTitle', { defaultValue: 'Error' }),
                        t('errorMessage', {
                          defaultValue: 'An error occurred while processing your request. Please try again later.',
                        }),
                      );
                    }
                  }
                }}
                colorScheme="destructive"
                left={<MaterialSymbol icon="cancel" size={18} />}
                style={itemStyle}
              >
                {t('Table.Row.DropdownTable.reject', { defaultValue: 'Reject' })}
              </DropdownMenu.Item>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu>
    );
  }
};
export default ApprovalInboxContainer;
